import { memo, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { inputValidationCheck } from '../../../../../../../validators/inputValidators/inputValidators'
import { useFormValidation } from '../../../../../../../hooks/useFormValidation'
import { getBlockDictKeyDataControlDescriptors } from '../../../../../BlockDictionary/lib/helpers'
import { buildControlsExt } from '../../../../../../inputs/controls'
import { buildElementsExt } from '../../../../../../GUIConstructor/control'
import { Button, Card } from 'react-bootstrap'
import AppButton from '../../../../../../buttons/AppButton/AppButton'
import RemoveItemBtn from '../../../../../../buttons/RemoveItemBtn/RemoveItemBtn'
import { getLabel } from '../../../../../../../utils/getLabel'
import { FormattedMessage } from 'react-intl'
import {
    addRuleBlockDictNewKey,
    deleteRuleBlockDictItem,
    initRuleEmptyData,
} from '../../../../../../../store/actions/ruleCardActions'

export interface Props {
    dataPath: string
    data?: any
    dataSchema: any
    name: string
    dictionaryKey: string
    value: string
    title?: string
    wrapper?: string
    setDictKeys: React.Dispatch<React.SetStateAction<string[]>>
}

export const RuleBlockDictCard = memo(function RuleBlockDictCard({
    dataPath,
    data = {},
    dataSchema,
    name,
    dictionaryKey,
    title,
    wrapper,
    setDictKeys,
}: Props) {
    const dispatch = useAppDispatch()
    const [isKeyEditing, setIsKeyEditing] = useState(false)
    const path = [...(dataPath.split(PATH_JOINER) || [])]
    const [initial] = useState({ [name]: name })
    const keySchema = inputValidationCheck(dataSchema.children.key.type, {
        ...dataSchema.children.key,
        is_required: true,
    })
    const [inputState, setInputState, touched, setTouched, validationResult] =
        useFormValidation(initial, { [name]: keySchema })

    const controlDescriptors = getBlockDictKeyDataControlDescriptors(
        inputState,
        dataSchema,
        name
    )

    const keyData = buildControlsExt(
        controlDescriptors,
        inputState,
        setInputState,
        touched,
        setTouched,
        validationResult
    )

    const isNewKeyValid =
        !Object.keys(validationResult).length && inputState[name] !== ''

    useEffect(() => {
        if (!data[name]) {
            dispatch(initRuleEmptyData({ schema: dataSchema, name, path }))
        }
        if (name === '') {
            setIsKeyEditing(true)
        }
    }, []) // eslint-disable-line

    const handleOnDelete = () => {
        dispatch(
            deleteRuleBlockDictItem({
                path,
                data,
                dictionaryKey,
            })
        )
        setDictKeys((prev) => prev.filter((dict) => dict !== dictionaryKey))
    }

    const handleOnEdit = () => {
        setIsKeyEditing((prev) => !prev)
    }

    const handleOnChangeKey = () => {
        if (name !== inputState[name]) {
            dispatch(
                addRuleBlockDictNewKey({
                    oldKey: name,
                    path: dataPath.split(PATH_JOINER),
                    data: {
                        ...data,
                        [inputState[name]]: data[initial[name]],
                    },
                })
            )
            setDictKeys((prev: string[]) => {
                const targetIndex = prev.indexOf(name)
                const keys = [...prev]
                keys[targetIndex] = inputState[name]
                return keys
            })
            setIsKeyEditing(false)
        }
    }

    const elementExt = buildElementsExt(
        path.join(PATH_JOINER),
        name,
        dataSchema.children.value,
        data,
        wrapper || 'block_dictionary_card'
    )

    return (
        <Card
            className="m-2 h-aut p-0"
            style={{ width: '35rem', minHeight: '100px' }}
        >
            <div className="d-flex">
                <AppButton
                    variant="edit"
                    onClick={handleOnEdit}
                    className="ms-auto me-0 text-secondary p-0"
                    tooltip="edit"
                />
                <RemoveItemBtn
                    data={{
                        body: `Remove ${dictionaryKey} from ${getLabel(
                            'dictionary',
                            title
                        )}?`,
                        onSubmit: handleOnDelete,
                    }}
                    className="mt-0 p-relative text-secondary"
                    position="relative"
                />
            </div>
            <Card.Body className="pt-0 pb-3 ps-2 pe-2 m-0 p-relative w-100">
                <div className="d-flex flex-column">
                    <div
                        style={{
                            color: '#324766',
                            fontWeight: 600,
                            maxWidth: '30%',
                        }}
                        className="d-flex align-items-center h5"
                    >
                        {!isKeyEditing ? (
                            `${dictionaryKey}:`
                        ) : (
                            <div className="d-flex align-items-center mb-2">
                                <div className="mw-250">{keyData}</div>
                                <Button
                                    variant="outline-primary"
                                    className="p-0 ps-2 pe-2 app-dictionary-row"
                                    disabled={!isNewKeyValid}
                                    onClick={handleOnChangeKey}
                                >
                                    <FormattedMessage id="buttons.add" />
                                </Button>
                                <Button
                                    className="p-0 ps-1 pe-1 ms-1 app-dictionary-row"
                                    variant="outline-secondary"
                                    onClick={handleOnEdit}
                                >
                                    <FormattedMessage id="buttons.cancel" />
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="border-top w-100">{elementExt}</div>
                </div>
            </Card.Body>
        </Card>
    )
})
